import { FormEvent, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, Form, Modal, Nav } from 'react-bootstrap';
import Select from 'react-select';
import { BiColumns, BiFile, BiInfoCircle, BiPrinter, BiSpreadsheet } from 'react-icons/bi';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Contrib_AreaDto,
  Contrib_ServidorDto,
  Defensor_DefensorDto,
} from '@/api.types';
import { TIPOS_ATENDIMENTO_SOLAR_OPCOES, TiposAtendimentosSolar } from '@/modules/reports/enum/tipos-atendimentos-solar.enum';
import {
  DefensoriaSolarOpcao,
  LinhaAtendimentoTitular,
  LinhaHistoricoLogin,
  OpcaoSolar,
  SolarService,
  UsuarioSolarLogin,
} from '@/modules/reports/solar.service';
import { CalendarioHistoricoLogin } from '@/modules/reports/presentation/components/CalendarioHistoricoLogin';
import Loading from '@/shared/components/Carregando';
import Links from '@/shared/constants/navegacao';
import { NavigationView } from '@/shared/constants/navigation-view.enum';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { useNavigationView } from '@/shared/hooks/useNavigationView';
import imprimirDiv from '@/shared/utils/impressao';
import { exportFromTableToPdf } from '@/shared/utils/pdf';
import { exportFromTableToExcel } from '@/shared/utils/xlsx';

const TABELA_ID = 'relatorios-solar-tabela';
const LOGO_DEFENSORIA = '/img/LOGO_DPE_2024_ok.png';
const CONTEUDO_ID = 'relatorios-solar-conteudo';
const DATA_INICIAL_MINIMA = '2000-01-01';
const DATA_FINAL_MAXIMA = '3000-12-31';
const solarService = new SolarService();
const RELATORIO_ATENDIMENTOS_TITULAR = 'atendimentos-titular';
const RELATORIO_HISTORICO_LOGIN = 'historico-login';
const RELATORIOS_DISPONIVEIS = [
  {
    id: RELATORIO_ATENDIMENTOS_TITULAR,
    nome: 'Atendimentos do titular',
  },
  {
    id: RELATORIO_HISTORICO_LOGIN,
    nome: 'Histórico Login',
  },
];

const GRUPOS_FILTRO = [
  {
    id: 'local',
    label: 'Local',
    itens: [
      { id: 'comarca', label: 'Comarca' },
      { id: 'predio', label: 'Prédio' },
      { id: 'defensoria', label: 'Defensoria' },
      { id: 'nucleo', label: 'Núcleo' },
    ],
  },
  {
    id: 'pessoas',
    label: 'Pessoas',
    itens: [
      { id: 'defensor', label: 'Defensor' },
      { id: 'servidor', label: 'Servidor' },
    ],
  },
  {
    id: 'parametros',
    label: 'Parâmetros',
    itens: [
      { id: 'area', label: 'Área' },
      { id: 'tipo', label: 'Tipo de Atendimento' },
    ],
  },
] as const;

type AbaFiltro = (typeof GRUPOS_FILTRO)[number]['itens'][number]['id'];

type CampoListaSolar =
  | 'tipoAtendimento'
  | 'comarcaId'
  | 'predioId'
  | 'defensoriaId'
  | 'nucleoId'
  | 'areaId'
  | 'defensorId'
  | 'servidorId'
  | 'usuarioId';

type FiltrosSolar = {
  dataInicial: string;
  dataFinal: string;
  tipoAtendimento: string[];
  comarcaId: string[];
  predioId: string[];
  defensoriaId: string[];
  nucleoId: string[];
  areaId: string[];
  defensorId: string[];
  servidorId: string[];
  usuarioId: string[];
  exibirCalendario: boolean;
};

type OpcaoFiltro = { value: string; label: string };

function copiarFiltros(filtros: FiltrosSolar): FiltrosSolar {
  return {
    ...filtros,
    tipoAtendimento: [...filtros.tipoAtendimento],
    comarcaId: [...filtros.comarcaId],
    predioId: [...filtros.predioId],
    defensoriaId: [...filtros.defensoriaId],
    nucleoId: [...filtros.nucleoId],
    areaId: [...filtros.areaId],
    defensorId: [...filtros.defensorId],
    servidorId: [...filtros.servidorId],
    usuarioId: [...filtros.usuarioId],
  };
}

function primeiroId(ids: string[]) {
  const valor = Number(ids[0]);
  return Number.isFinite(valor) && valor > 0 ? valor : 0;
}

function dataIso(data: Date) {
  const ano = data.getFullYear();
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${ano}-${mes}-${dia}`;
}

function periodoMesAtual() {
  const agora = new Date();
  const inicio = new Date(agora.getFullYear(), agora.getMonth(), 1);
  const fim = new Date(agora.getFullYear(), agora.getMonth() + 1, 0);
  return { dataInicial: dataIso(inicio), dataFinal: dataIso(fim) };
}

function filtrosVazios(): FiltrosSolar {
  const periodo = periodoMesAtual();
  return {
    dataInicial: periodo.dataInicial,
    dataFinal: periodo.dataFinal,
    tipoAtendimento: [],
    comarcaId: [],
    predioId: [],
    defensoriaId: [],
    nucleoId: [],
    areaId: [],
    defensorId: [],
    servidorId: [],
    usuarioId: [],
    exibirCalendario: false,
  };
}

function periodoMaiorQueUmMes(inicio: string, fim: string): boolean {
  const ini = partesData(inicio);
  const fin = partesData(fim);
  if (!ini || !fin) return true;
  const limite = new Date(Date.UTC(ini.ano, ini.mes - 1 + 1, ini.dia));
  const final = new Date(Date.UTC(fin.ano, fin.mes - 1, fin.dia));
  return final.getTime() > limite.getTime();
}

function periodoMaiorQueUmAno(inicio: string, fim: string): boolean {
  const ini = partesData(inicio);
  const fin = partesData(fim);
  if (!ini || !fin) return true;
  const ultimoDia = new Date(Date.UTC(ini.ano + 1, ini.mes, 0)).getUTCDate();
  const limite = new Date(Date.UTC(ini.ano + 1, ini.mes - 1, Math.min(ini.dia, ultimoDia)));
  const final = new Date(Date.UTC(fin.ano, fin.mes - 1, fin.dia));
  return final.getTime() > limite.getTime();
}

function partesData(valor: string) {
  const [ano, mes, dia] = valor.slice(0, 10).split('-').map(Number);
  if (!Number.isFinite(ano) || !Number.isFinite(mes) || !Number.isFinite(dia)) return null;
  return { ano, mes, dia };
}

function dataAmigavel(valor: string) {
  const partes = partesData(valor);
  if (!partes) return valor;
  const dia = String(partes.dia).padStart(2, '0');
  const mes = String(partes.mes).padStart(2, '0');
  return `${dia}/${mes}/${partes.ano}`;
}

function textoSelecionados(ids: string[], opcoes: OpcaoFiltro[]) {
  if (ids.length === 0) return null;
  if (ids.length === 1) return opcoes.find((opcao) => opcao.value === ids[0])?.label ?? ids[0];
  return String(ids.length);
}

function trechoFiltro(rotulo: string, ids: string[], opcoes: OpcaoFiltro[]) {
  const texto = textoSelecionados(ids, opcoes);
  if (!texto) return null;
  return `${rotulo}: ${texto}`;
}

function textoCabecalho(ids: string[], opcoes: OpcaoFiltro[], vazio: string) {
  const texto = textoSelecionados(ids, opcoes);
  if (!texto) return vazio;
  return texto.toLocaleUpperCase('pt-BR');
}

function horaSolar(valor?: string) {
  if (!valor) return '';
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return '';
  return data.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Maceio',
  });
}

function dataCurta(valor: string) {
  const match = String(valor).match(/^(\d{2}\/\d{2}\/)(\d{4})$/);
  if (!match) return valor;
  return `${match[1]}${match[2].slice(2)}`;
}

function celulaData(linha: LinhaAtendimentoTitular) {
  const data = dataCurta(linha.data_atendimento);
  const inicio = horaSolar(linha.inicio);
  const termino = horaSolar(linha.termino);
  if (inicio && termino) return `${data} ${inicio} - ${termino}`;
  return data;
}

function nomeSimplificado(nome: string | null | undefined) {
  const partes = String(nome ?? '').trim().split(/\s+/).filter(Boolean);
  if (partes.length <= 2) return partes.join(' ');
  return `${partes[0]} ${partes[partes.length - 1]}`;
}

function celulaQualificacao(linha: LinhaAtendimentoTitular) {
  const qualificacao = linha.qualificacao_nome?.trim() || '';
  const anotacao = linha.historico_recepcao?.trim() || '';
  if (qualificacao && anotacao) return `${qualificacao} / ${anotacao}`;
  return qualificacao || anotacao;
}

const COLUNAS_RELATORIO = [
  { id: 'ordem', label: 'Nº', cabecalho: '', valor: (linha: LinhaAtendimentoTitular, indice: number) => linha.row_number ?? indice + 1 },
  { id: 'requerente', label: 'Requerente', cabecalho: 'Requerente', valor: (linha: LinhaAtendimentoTitular) => linha.requerente_nome },
  { id: 'telefones', label: 'Telefones', cabecalho: 'Telefones', valor: (linha: LinhaAtendimentoTitular) => linha.telefone_numero },
  { id: 'qualificacao', label: 'Qualificação / Anotação', cabecalho: 'Qualificação / Anotação', valor: celulaQualificacao },
  { id: 'atendido', label: 'Atendido por', cabecalho: 'Atendido por', valor: (linha: LinhaAtendimentoTitular) => linha.atendido_por_nome },
  { id: 'data', label: 'Data', cabecalho: 'Data', valor: celulaData },
  { id: 'numero', label: 'Número', cabecalho: 'Número', valor: (linha: LinhaAtendimentoTitular) => linha.atendimento_numero },
  { id: 'tipo', label: 'Tipo', cabecalho: 'Tipo', valor: (linha: LinhaAtendimentoTitular) => linha.tipo_nome },
] as const;

const COLUNAS_LOGIN = [
  { id: 'data', label: 'Data do login', cabecalho: 'Data do login', valor: (linha: LinhaHistoricoLogin) => linha.cadastrado_em },
  { id: 'logout', label: 'Logout', cabecalho: 'Logout', valor: (linha: LinhaHistoricoLogin) => linha.logout },
  { id: 'usuario', label: 'Usuário', cabecalho: 'Usuário', valor: (linha: LinhaHistoricoLogin) => linha.username },
  { id: 'nome', label: 'Nome', cabecalho: 'Nome', valor: (linha: LinhaHistoricoLogin) => linha.nome },
  { id: 'ip', label: 'IP', cabecalho: 'IP', valor: (linha: LinhaHistoricoLogin) => linha.endereco_ip },
  { id: 'navegador', label: 'Navegador', cabecalho: 'Navegador', valor: (linha: LinhaHistoricoLogin) => linha.info_navegador },
] as const;

type ColunaRelatorioId = (typeof COLUNAS_RELATORIO)[number]['id'];
type ColunaLoginId = (typeof COLUNAS_LOGIN)[number]['id'];

function colunasPadrao(): ColunaRelatorioId[] {
  return COLUNAS_RELATORIO.map((coluna) => coluna.id);
}

function agruparLinhas(linhas: LinhaAtendimentoTitular[]) {
  const grupos: {
    defensoria: string;
    defensor: string;
    substituto: string;
    linhas: LinhaAtendimentoTitular[];
  }[] = [];
  linhas.forEach((linha) => {
    const ultimo = grupos[grupos.length - 1];
    if (!ultimo || ultimo.defensoria !== linha.defensoria_nome || ultimo.defensor !== linha.defensor_nome) {
      grupos.push({
        defensoria: linha.defensoria_nome,
        defensor: linha.defensor_nome,
        substituto: linha.substituto_nome?.trim() || '',
        linhas: [linha],
      });
      return;
    }
    ultimo.linhas.push(linha);
    if (!ultimo.substituto && linha.substituto_nome?.trim()) ultimo.substituto = linha.substituto_nome.trim();
  });
  return grupos;
}

function totaisGrupo(linhas: LinhaAtendimentoTitular[]) {
  const principais = new Set<number>([
    TiposAtendimentosSolar.INICIAL,
    TiposAtendimentosSolar.RETORNO,
    TiposAtendimentosSolar.NUCLEO,
  ]);
  const ligacao = linhas.filter((linha) => linha.por_ligacao).length;
  const whatsapp = linhas.filter((linha) => linha.por_whatsapp).length;
  const email = linhas.filter((linha) => linha.por_email).length;
  const presencial = linhas.filter((linha) => linha.presencial).length;
  const remoto = linhas.filter(
    (linha) => !linha.presencial && !linha.por_ligacao && !linha.por_whatsapp && !linha.por_email,
  ).length;
  return {
    ligacao,
    whatsapp,
    email,
    remoto,
    presencial,
    naoPresencial: linhas.length - presencial,
    civil: linhas.filter((linha) => !linha.area_penal).length,
    criminal: linhas.filter((linha) => linha.area_penal).length,
    principais: linhas.filter((linha) => principais.has(Number(linha.tipo))).length,
    visitas: linhas.filter((linha) => Number(linha.tipo) === TiposAtendimentosSolar.VISITA).length,
    total: linhas.length,
  };
}

function esperar(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const VISOES_RELATORIO_SOLAR = new Set<NavigationView>([
  NavigationView.Administrador,
  NavigationView.Corregedoria,
  NavigationView.Subcorregedoria,
  NavigationView.GestorRelatorios,
  NavigationView.Defensor,
  NavigationView.DefensorDiretor,
]);

export default function RelatoriosSolarPage() {
  const { effectiveView, viewReady } = useNavigationView();

  if (!viewReady) return <Loading />;
  if (effectiveView == null || !VISOES_RELATORIO_SOLAR.has(effectiveView)) {
    return <Navigate to={Links.Nao_Autorizado} replace />;
  }

  return <RelatoriosSolarConteudo />;
}

function RelatoriosSolarConteudo() {
  const { effectiveView } = useNavigationView();
  const navigate = useNavigate();
  const { setActions } = useBreadcrumbActions();
  const [listaAberta, setListaAberta] = useState(false);
  const [relatorioPendente, setRelatorioPendente] = useState<string | null>(null);
  const [modalAberto, setModalAberto] = useState(false);
  const [aba, setAba] = useState<AbaFiltro>('defensor');
  const [rascunho, setRascunho] = useState<FiltrosSolar>(filtrosVazios);
  const [filtrosAplicados, setFiltrosAplicados] = useState<FiltrosSolar | null>(null);
  const [comarcas, setComarcas] = useState<OpcaoSolar[]>([]);
  const [nucleos, setNucleos] = useState<OpcaoSolar[]>([]);
  const [defensorias, setDefensorias] = useState<DefensoriaSolarOpcao[]>([]);
  const [defensores, setDefensores] = useState<Defensor_DefensorDto[]>([]);
  const [servidores, setServidores] = useState<Contrib_ServidorDto[]>([]);
  const [areas, setAreas] = useState<Contrib_AreaDto[]>([]);
  const [predios, setPredios] = useState<Contrib_AreaDto[]>([]);
  const [usuariosLogin, setUsuariosLogin] = useState<UsuarioSolarLogin[]>([]);
  const [listasCarregadas, setListasCarregadas] = useState(false);
  const [relatorioId, setRelatorioId] = useState<string | null>(null);
  const [linhas, setLinhas] = useState<LinhaAtendimentoTitular[]>([]);
  const [linhasLogin, setLinhasLogin] = useState<LinhaHistoricoLogin[]>([]);
  const [colunasVisiveis, setColunasVisiveis] = useState<ColunaRelatorioId[]>(colunasPadrao);
  const [colunasLoginVisiveis, setColunasLoginVisiveis] = useState<ColunaLoginId[]>(COLUNAS_LOGIN.map((coluna) => coluna.id));
  const [simplificarAtendido, setSimplificarAtendido] = useState(false);
  const [carregandoRelatorio, setCarregandoRelatorio] = useState(false);
  const [infoVisibilidade, setInfoVisibilidade] = useState(false);
  const [relatorioHabilitado, setRelatorioHabilitado] = useState(true);
  const consultaSeq = useRef(0);

  const ehAdmin = effectiveView === NavigationView.Administrador;
  const relatoriosVisiveis = RELATORIOS_DISPONIVEIS.filter(
    (item) => item.id !== RELATORIO_HISTORICO_LOGIN || ehAdmin,
  );
  const relatorio = relatoriosVisiveis.find((item) => item.id === relatorioId) ?? null;
  const resultadoPronto = filtrosAplicados !== null && !carregandoRelatorio && relatorioId !== null;

  useEffect(() => {
    if (!modalAberto || listasCarregadas) return;

    Promise.allSettled([
      solarService.enumerateComarcas(),
      solarService.enumerateNucleos(),
      solarService.enumerateDefensorias(),
      solarService.enumerateDefensores(),
      solarService.enumerateServidores(),
      solarService.enumerateAreas(),
      solarService.enumeratePredios(),
      solarService.enumerateUsuariosLogin(),
    ]).then((resultados) => {
      const [
        comarcasLista,
        nucleosLista,
        defensoriasLista,
        defensoresLista,
        servidoresLista,
        areasLista,
        prediosLista,
        usuariosLista,
      ] = resultados;
      if (comarcasLista.status === 'fulfilled') setComarcas(comarcasLista.value);
      if (nucleosLista.status === 'fulfilled') setNucleos(nucleosLista.value);
      if (defensoriasLista.status === 'fulfilled') setDefensorias(defensoriasLista.value);
      if (defensoresLista.status === 'fulfilled') setDefensores(defensoresLista.value);
      if (servidoresLista.status === 'fulfilled') setServidores(servidoresLista.value);
      if (areasLista.status === 'fulfilled') setAreas(areasLista.value);
      if (prediosLista.status === 'fulfilled') setPredios(prediosLista.value);
      if (usuariosLista.status === 'fulfilled') setUsuariosLogin(usuariosLista.value);
      const falha = resultados.find((resultado) => resultado.status === 'rejected');
      if (falha?.status === 'rejected') toast.error(String(falha.reason));
      setListasCarregadas(true);
    });
  }, [modalAberto, listasCarregadas]);

  const abrirListaRef = useRef<() => void>(() => {});

  function abrirLista() {
    setListaAberta(true);
  }

  abrirListaRef.current = abrirLista;

  useEffect(() => {
    setActions({
      primary: {
        label: 'Novo Relatório',
        variant: 'primary',
        onClick: () => abrirListaRef.current(),
      },
      secondary: {
        label: 'Relatórios Salvos',
        onClick: () => {},
      },
      ...(ehAdmin
        ? {
          tertiary: {
            label: 'Correlação',
            onClick: () => navigate(Links.Relatorios_Solar_Correlacao),
          },
        }
        : {}),
    });
    return () => setActions({});
  }, [setActions, ehAdmin, navigate]);

  function escolherRelatorio(id: string) {
    if (id === RELATORIO_HISTORICO_LOGIN && !ehAdmin) return;
    if (id === RELATORIO_ATENDIMENTOS_TITULAR && !relatorioHabilitado) return;
    setRelatorioPendente(id);
    setListaAberta(false);
  }

  function abrirFiltrosAoFecharLista() {
    if (!relatorioPendente) return;
    const id = relatorioPendente;
    setRelatorioPendente(null);
    setRelatorioId(id);
    setAba('defensor');
    setRascunho(filtrosAplicados ? copiarFiltros(filtrosAplicados) : filtrosVazios());
    setModalAberto(true);
  }

  function limparRascunho() {
    setRascunho(filtrosVazios());
  }

  function aplicarFiltros(event: FormEvent) {
    event.preventDefault();
    if (relatorioId === RELATORIO_HISTORICO_LOGIN) {
      if (rascunho.usuarioId.length !== 1) {
        toast.error('Escolha um usuário.');
        return;
      }
      if (periodoMaiorQueUmAno(rascunho.dataInicial, rascunho.dataFinal)) {
        toast.error('O período não pode passar de 1 ano.');
        return;
      }
    } else if (periodoBloqueado) {
      toast.error('Sem defensoria ou núcleo, o período não pode passar de 1 mês.');
      return;
    }
    const aplicados = copiarFiltros(rascunho);
    setFiltrosAplicados(aplicados);
    setModalAberto(false);
    if (!relatorioId) return;
    void executarRelatorio(relatorioId, aplicados);
  }

  function textoParametros(filtros: FiltrosSolar) {
    if (relatorioId === RELATORIO_HISTORICO_LOGIN) {
      return [
        `Período: ${dataAmigavel(filtros.dataInicial)} a ${dataAmigavel(filtros.dataFinal)}`,
        trechoFiltro('Usuário', filtros.usuarioId, opcoesUsuario),
        filtros.exibirCalendario ? 'Calendário: sim' : null,
      ].filter((item): item is string => Boolean(item)).join('. ');
    }
    return [
      `Período: ${dataAmigavel(filtros.dataInicial)} a ${dataAmigavel(filtros.dataFinal)}`,
      trechoFiltro('Defensor', filtros.defensorId, opcoesDefensor),
      trechoFiltro('Servidor', filtros.servidorId, opcoesServidor),
      trechoFiltro('Tipo de atendimento', filtros.tipoAtendimento, opcoesTipo),
      trechoFiltro('Área', filtros.areaId, opcoesArea),
      trechoFiltro('Comarca', filtros.comarcaId, opcoesComarca),
      trechoFiltro('Prédio', filtros.predioId, opcoesPredio),
      trechoFiltro('Núcleo', filtros.nucleoId, opcoesNucleo),
      trechoFiltro('Defensoria', filtros.defensoriaId, opcoesDefensoria),
    ].filter((item): item is string => Boolean(item)).join('. ');
  }

  async function executarRelatorio(id: string, filtros: FiltrosSolar) {
    if (id === RELATORIO_HISTORICO_LOGIN && !ehAdmin) return;
    if (id !== RELATORIO_ATENDIMENTOS_TITULAR && id !== RELATORIO_HISTORICO_LOGIN) return;
    const seq = ++consultaSeq.current;
    setCarregandoRelatorio(true);
    setLinhas([]);
    setLinhasLogin([]);
    if (id === RELATORIO_HISTORICO_LOGIN) {
      try {
        const job = await solarService.enfileirarHistoricoLogin({
          data_inicial: filtros.dataInicial,
          data_final: filtros.dataFinal,
          usuario_id: primeiroId(filtros.usuarioId),
          filtros_texto: textoParametros(filtros),
        });
        while (consultaSeq.current === seq) {
          const atual = await solarService.consultarHistoricoLogin(job.id);
          if (atual.status === 'concluido') {
            setLinhasLogin(atual.dados ?? []);
            return;
          }
          if (atual.status === 'falha') {
            throw new Error(atual.erro || 'Falha ao montar o relatório.');
          }
          await esperar(1500);
        }
      } catch (error) {
        if (consultaSeq.current !== seq) return;
        setLinhasLogin([]);
        toast.error(error instanceof Error ? error.message : 'Falha ao consultar o relatório.');
      } finally {
        if (consultaSeq.current === seq) setCarregandoRelatorio(false);
      }
      return;
    }
    try {
      const job = await solarService.enfileirarAtendimentosTitular({
        p_area_id: primeiroId(filtros.areaId),
        p_defensoria_id: primeiroId(filtros.defensoriaId),
        p_defensor_id: primeiroId(filtros.defensorId),
        p_servidor_id: primeiroId(filtros.servidorId),
        p_tipo_atendimento: primeiroId(filtros.tipoAtendimento),
        p_nucleo_id: primeiroId(filtros.nucleoId),
        p_data_inicial: filtros.dataInicial,
        p_data_final: filtros.dataFinal,
        filtros_texto: textoParametros(filtros),
      });
      while (consultaSeq.current === seq) {
        const atual = await solarService.consultarAtendimentosTitular(job.id);
        if (atual.status === 'concluido') {
          setLinhas(atual.dados ?? []);
          return;
        }
        if (atual.status === 'falha') {
          throw new Error(atual.erro || 'Falha ao montar o relatório.');
        }
        await esperar(1500);
      }
    } catch (error) {
      if (consultaSeq.current !== seq) return;
      setLinhas([]);
      toast.error(error instanceof Error ? error.message : 'Falha ao consultar o relatório.');
    } finally {
      if (consultaSeq.current === seq) setCarregandoRelatorio(false);
    }
  }

  const defensoriasDaComarca = useMemo(() => {
    if (rascunho.comarcaId.length === 0) return defensorias;
    const ids = new Set(rascunho.comarcaId);
    return defensorias.filter((item) => ids.has(String(item.comarcaId ?? '')));
  }, [defensorias, rascunho.comarcaId]);

  const nucleosVisiveis = useMemo(() => {
    if (rascunho.comarcaId.length === 0) return nucleos;
    const ids = new Set(
      defensoriasDaComarca
        .map((item) => item.nucleoId)
        .filter((id): id is number => id != null)
        .map(String),
    );
    return nucleos.filter((item) => ids.has(String(item.id)));
  }, [nucleos, defensoriasDaComarca, rascunho.comarcaId]);

  const defensoriasVisiveis = useMemo(() => {
    if (rascunho.nucleoId.length === 0) return defensoriasDaComarca;
    const ids = new Set(rascunho.nucleoId);
    return defensoriasDaComarca.filter((item) => ids.has(String(item.nucleoId ?? '')));
  }, [defensoriasDaComarca, rascunho.nucleoId]);

  const opcoesDefensor = defensores.map((item) => ({
    value: String(item.id),
    label: item.servidor?.nome ?? String(item.id),
  }));
  const opcoesServidor = servidores.map((item) => ({ value: String(item.id), label: item.nome }));
  const opcoesTipo = TIPOS_ATENDIMENTO_SOLAR_OPCOES.map((item) => ({
    value: String(item.value),
    label: item.label,
  }));
  const opcoesArea = areas.map((item) => ({ value: String(item.id), label: item.nome }));
  const opcoesComarca = comarcas.map((item) => ({ value: String(item.id), label: item.nome }));
  const opcoesPredio = predios.map((item) => ({ value: String(item.id), label: item.nome }));
  const opcoesDefensoria = defensoriasVisiveis.map((item) => ({
    value: String(item.id),
    label: item.nome,
  }));
  const opcoesNucleo = nucleosVisiveis.map((item) => ({ value: String(item.id), label: item.nome }));
  const opcoesUsuario = usuariosLogin
    .map((item) => ({
      value: String(item.id),
      label: item.nome ? `${item.username} — ${item.nome}` : item.username,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' }));
  const usuarioSelecionado = opcoesUsuario.find((opcao) => opcao.value === rascunho.usuarioId[0]) ?? null;
  const ehHistoricoLogin = relatorioId === RELATORIO_HISTORICO_LOGIN;

  const periodoBloqueado = ehHistoricoLogin
    ? rascunho.usuarioId.length !== 1 || periodoMaiorQueUmAno(rascunho.dataInicial, rascunho.dataFinal)
    : rascunho.defensoriaId.length === 0 &&
      rascunho.nucleoId.length === 0 &&
      periodoMaiorQueUmMes(rascunho.dataInicial, rascunho.dataFinal);

  function limitarData(valor: string) {
    if (!valor || valor < DATA_INICIAL_MINIMA) return DATA_INICIAL_MINIMA;
    if (valor > DATA_FINAL_MAXIMA) return DATA_FINAL_MAXIMA;
    return valor;
  }

  function atualizarData(campo: 'dataInicial' | 'dataFinal', valor: string) {
    setRascunho((atual) => ({ ...atual, [campo]: limitarData(valor) }));
  }

  function alternarLista(campo: CampoListaSolar, valor: string) {
    setRascunho((atual) => {
      const selecionados = atual[campo].includes(valor)
        ? atual[campo].filter((item) => item !== valor)
        : [...atual[campo], valor];
      const proximo = { ...atual, [campo]: selecionados };
      if (campo === 'comarcaId' || campo === 'nucleoId') return ajustarLocal(proximo);
      return proximo;
    });
  }

  function ajustarLocal(proximo: FiltrosSolar): FiltrosSolar {
    const comarcasSelecionadas = new Set(proximo.comarcaId);
    const daComarca = comarcasSelecionadas.size
      ? defensorias.filter((item) => comarcasSelecionadas.has(String(item.comarcaId ?? '')))
      : defensorias;
    const nucleosValidos = new Set(
      daComarca
        .map((item) => item.nucleoId)
        .filter((id): id is number => id != null)
        .map(String),
    );
    const nucleoId = proximo.comarcaId.length
      ? proximo.nucleoId.filter((id) => nucleosValidos.has(id))
      : proximo.nucleoId;
    const nucleosSelecionados = new Set(nucleoId);
    const defensoriasValidas = daComarca.filter(
      (item) => nucleosSelecionados.size === 0 || nucleosSelecionados.has(String(item.nucleoId ?? '')),
    );
    const idsDefensoria = new Set(defensoriasValidas.map((item) => String(item.id)));
    const defensoriaId = proximo.defensoriaId.filter((id) => idsDefensoria.has(id));
    return { ...proximo, nucleoId, defensoriaId };
  }

  const tituloRelatorio = relatorio?.nome ?? 'Relatórios do Solar';
  const colunasAtivas = COLUNAS_RELATORIO.filter((coluna) => colunasVisiveis.includes(coluna.id));
  const colunasLoginAtivas = COLUNAS_LOGIN.filter((coluna) => colunasLoginVisiveis.includes(coluna.id));
  const temLinhas = ehHistoricoLogin ? linhasLogin.length > 0 : linhas.length > 0;

  function alternarColuna(id: ColunaRelatorioId) {
    setColunasVisiveis((atual) => {
      if (atual.includes(id)) {
        if (atual.length === 1) return atual;
        return atual.filter((item) => item !== id);
      }
      return colunasPadrao().filter((item) => item === id || atual.includes(item));
    });
  }

  function alternarColunaLogin(id: ColunaLoginId) {
    setColunasLoginVisiveis((atual) => {
      if (atual.includes(id)) {
        if (atual.length === 1) return atual;
        return atual.filter((item) => item !== id);
      }
      return COLUNAS_LOGIN.map((coluna) => coluna.id).filter((item) => item === id || atual.includes(item));
    });
  }

  const resumoFiltros = textoParametros(rascunho);

  return (
    <div id={CONTEUDO_ID}>
        <div className="d-flex justify-content-between align-items-center gap-2 mb-3 d-print-none">
          <h3 className="h5 mb-0">Relatórios do Solar</h3>
          {resultadoPronto && temLinhas && (
            <div className="d-flex gap-2">
              <Dropdown align="end" autoClose="outside">
                <Dropdown.Toggle variant="outline-secondary" size="sm">
                  <BiColumns className="me-1" />
                  Colunas
                </Dropdown.Toggle>
                <Dropdown.Menu className="relatorio-solar-colunas-menu px-3 py-2">
                  {ehHistoricoLogin
                    ? COLUNAS_LOGIN.map((coluna) => (
                      <Form.Check
                        key={coluna.id}
                        type="checkbox"
                        id={`coluna-solar-${coluna.id}`}
                        className="py-1 user-select-none"
                        label={coluna.label}
                        checked={colunasLoginVisiveis.includes(coluna.id)}
                        onChange={() => alternarColunaLogin(coluna.id)}
                      />
                    ))
                    : COLUNAS_RELATORIO.map((coluna) => (
                      <Form.Check
                        key={coluna.id}
                        type="checkbox"
                        id={`coluna-solar-${coluna.id}`}
                        className="py-1 user-select-none"
                        label={coluna.label}
                        checked={colunasVisiveis.includes(coluna.id)}
                        onChange={() => alternarColuna(coluna.id)}
                      />
                    ))}
                  {!ehHistoricoLogin && (
                    <>
                  <hr className="my-2" />
                  <Form.Check
                    type="checkbox"
                    id="coluna-solar-simplificar-atendido"
                    className="py-1 user-select-none"
                    label="Simplificar Atendido por"
                    checked={simplificarAtendido}
                    onChange={() => setSimplificarAtendido((atual) => !atual)}
                  />
                    </>
                  )}
                </Dropdown.Menu>
              </Dropdown>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => exportFromTableToExcel(TABELA_ID, 'relatorios-solar.xlsx')}
              >
                <BiSpreadsheet className="me-1" />
                Excel
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => exportFromTableToPdf(TABELA_ID, [tituloRelatorio], 'l')}
              >
                <BiFile className="me-1" />
                PDF
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() =>
                  imprimirDiv(CONTEUDO_ID, {
                    titulo: tituloRelatorio,
                    subtitulo: 'Consulta direta ao Solar',
                  })
                }
              >
                <BiPrinter className="me-1" />
                Imprimir
              </Button>
            </div>
          )}
        </div>

        {carregandoRelatorio ? (
          <div className="text-center text-muted py-5 border rounded">
            <Loading />
            <p className="mb-0 mt-3">O relatório está sendo montado.</p>
          </div>
        ) : temLinhas && filtrosAplicados && ehHistoricoLogin ? (
          <article className="relatorio-solar-folha">
            <header className="relatorio-solar-cabecalho">
              <img src={LOGO_DEFENSORIA} alt="Defensoria Pública do Estado de Sergipe" width={72} />
              <div className="flex-grow-1">
                <h2>DEFENSORIA PÚBLICA DO ESTADO DE SERGIPE</h2>
                <h3>CORREGEDORIA ESTATÍSTICA</h3>
                <p>
                  Histórico Login - {dataAmigavel(filtrosAplicados.dataInicial)} à{' '}
                  {dataAmigavel(filtrosAplicados.dataFinal)}
                </p>
              </div>
            </header>
            <div className="relatorio-solar-meta">
              <span>Usuário: {textoCabecalho(filtrosAplicados.usuarioId, opcoesUsuario, 'TODOS')}</span>
            </div>
            <table id={TABELA_ID} className="relatorio-solar-tabela">
              <thead>
                <tr>
                  {colunasLoginAtivas.map((coluna) => (
                    <th key={coluna.id}>{coluna.cabecalho}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {linhasLogin.map((linha, indice) => (
                  <tr
                    key={`${linha.usuario_id}-${linha.cadastrado_em}-${indice}`}
                    className={indice % 2 === 1 ? 'relatorio-solar-linha-alt' : undefined}
                  >
                    {colunasLoginAtivas.map((coluna) => (
                      <td key={coluna.id}>{coluna.valor(linha)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {filtrosAplicados.exibirCalendario && (
              <CalendarioHistoricoLogin
                key={`${filtrosAplicados.dataInicial}-${filtrosAplicados.dataFinal}`}
                linhas={linhasLogin}
                dataInicial={filtrosAplicados.dataInicial}
                dataFinal={filtrosAplicados.dataFinal}
              />
            )}
          </article>
        ) : linhas.length > 0 && filtrosAplicados ? (
          <article className="relatorio-solar-folha">
            <header className="relatorio-solar-cabecalho">
              <img src={LOGO_DEFENSORIA} alt="Defensoria Pública do Estado de Sergipe" width={72} />
              <div className="flex-grow-1">
                <h2>DEFENSORIA PÚBLICA DO ESTADO DE SERGIPE</h2>
                <h3>CORREGEDORIA ESTATÍSTICA</h3>
                <p>
                  Relatório de Atendimentos - {dataAmigavel(filtrosAplicados.dataInicial)} à{' '}
                  {dataAmigavel(filtrosAplicados.dataFinal)}
                </p>
              </div>
            </header>
            <div className="relatorio-solar-meta">
              <span>Defensor(a): {textoCabecalho(filtrosAplicados.defensorId, opcoesDefensor, 'TODOS')}</span>
              <span>Atendido por: {textoCabecalho(filtrosAplicados.servidorId, opcoesServidor, 'TODOS')}</span>
              <span>Defensoria: {textoCabecalho(filtrosAplicados.defensoriaId, opcoesDefensoria, 'TODAS')}</span>
              <span>Tipo de atendimento: {textoCabecalho(filtrosAplicados.tipoAtendimento, opcoesTipo, 'TODOS')}</span>
              <span>Área: {textoCabecalho(filtrosAplicados.areaId, opcoesArea, 'TODAS')}</span>
            </div>
            <table id={TABELA_ID} className="relatorio-solar-tabela">
              <tbody>
                {agruparLinhas(linhas).map((grupo) => {
                  const totais = totaisGrupo(grupo.linhas);
                  return (
                    <Fragment key={`${grupo.defensoria}-${grupo.defensor}`}>
                      <tr>
                        <td className="relatorio-solar-grupo" colSpan={colunasAtivas.length}>
                          Defensoria: {grupo.defensoria}
                        </td>
                      </tr>
                      <tr>
                        <td className="relatorio-solar-grupo" colSpan={colunasAtivas.length}>
                          Defensor: {grupo.defensor}
                          {grupo.substituto ? ` / Substituto: ${grupo.substituto}` : ''}
                        </td>
                      </tr>
                      <tr>
                        {colunasAtivas.map((coluna) => (
                          <th key={coluna.id}>{coluna.cabecalho}</th>
                        ))}
                      </tr>
                      {grupo.linhas.map((linha, indice) => (
                        <tr
                          key={`${linha.atendimento_numero}-${indice}`}
                          className={indice % 2 === 1 ? 'relatorio-solar-linha-alt' : undefined}
                        >
                          {colunasAtivas.map((coluna) => (
                            <td key={coluna.id}>
                              {coluna.id === 'atendido' && simplificarAtendido
                                ? nomeSimplificado(linha.atendido_por_nome)
                                : coluna.valor(linha, indice)}
                            </td>
                          ))}
                        </tr>
                      ))}
                      <tr>
                        <td colSpan={colunasAtivas.length} className="relatorio-solar-totais-celula">
                          <table className="relatorio-solar-totais">
                            <tbody>
                              <tr>
                                <td>Ligação</td>
                                <td className="relatorio-solar-totais-valor">{totais.ligacao}</td>
                                <td>Remoto</td>
                                <td className="relatorio-solar-totais-valor">{totais.remoto}</td>
                                <td>Total Geral - Civil</td>
                                <td className="relatorio-solar-totais-valor">{totais.civil}</td>
                                <td>Total Presencial</td>
                                <td className="relatorio-solar-totais-valor">{totais.presencial}</td>
                                <td className="relatorio-solar-totais-destaque">Iniciais, Retornos, Apoios</td>
                                <td className="relatorio-solar-totais-destaque relatorio-solar-totais-valor">{totais.principais}</td>
                              </tr>
                              <tr>
                                <td>WhatsApp</td>
                                <td className="relatorio-solar-totais-valor">{totais.whatsapp}</td>
                                <td>E-mail</td>
                                <td className="relatorio-solar-totais-valor">{totais.email}</td>
                                <td>Total Geral - Criminal</td>
                                <td className="relatorio-solar-totais-valor">{totais.criminal}</td>
                                <td>Total Não-presencial</td>
                                <td className="relatorio-solar-totais-valor">{totais.naoPresencial}</td>
                                <td className="relatorio-solar-totais-destaque">Visitas aos Presos</td>
                                <td className="relatorio-solar-totais-destaque relatorio-solar-totais-valor">{totais.visitas}</td>
                              </tr>
                              <tr>
                                <td colSpan={8} />
                                <td className="relatorio-solar-totais-destaque">TOTAL GERAL</td>
                                <td className="relatorio-solar-totais-destaque relatorio-solar-totais-valor">{totais.total}</td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </article>
        ) : filtrosAplicados && relatorioId ? (
          <div className="text-center text-muted py-5 border rounded">
            {ehHistoricoLogin
              ? 'Nenhum login encontrado para os filtros.'
              : 'Nenhum atendimento encontrado para os filtros.'}
          </div>
        ) : (
          <div className="text-center text-muted py-5 border rounded">
            Use Novo Relatório no topo para montar um relatório.
          </div>
        )}

      <Modal
        show={listaAberta}
        onHide={() => setListaAberta(false)}
        onExited={abrirFiltrosAoFecharLista}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Novo relatório</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="list-group">
            {relatoriosVisiveis.map((item) => (
              <div key={item.id} className="list-group-item d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-link text-start text-decoration-none flex-grow-1 p-0"
                  disabled={item.id === RELATORIO_ATENDIMENTOS_TITULAR && !relatorioHabilitado}
                  onClick={() => escolherRelatorio(item.id)}
                >
                  {item.nome}
                </button>
                {effectiveView === NavigationView.Administrador && (
                  <button
                    type="button"
                    className="btn btn-link p-0 text-secondary"
                    aria-label="Quem pode ver este relatório"
                    onClick={() => setInfoVisibilidade(true)}
                  >
                    <BiInfoCircle size={18} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={infoVisibilidade} onHide={() => setInfoVisibilidade(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Atendimentos do titular</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">Exibe os atendimentos realizados no Solar.</p>
          <Button
            size="sm"
            variant={relatorioHabilitado ? 'outline-danger' : 'outline-success'}
            onClick={() => setRelatorioHabilitado((atual) => !atual)}
          >
            {relatorioHabilitado ? 'Desabilitar' : 'Habilitar'}
          </Button>
          <p className="fw-semibold mb-1 mt-3">Perfis que podem usar:</p>
          <ul>
            <li>Administrador.</li>
            <li>Corregedoria.</li>
          </ul>
          <p className="fw-semibold mb-1">Perfis com visão limitada:</p>
          <ul>
            <li>Defensor - Apenas no período que foi lotado.</li>
            <li>Colaborador (assessor).</li>
          </ul>
          <p className="fw-semibold mb-1">Perfis sem acesso:</p>
          <ul className="mb-0">
            <li>Estagiários.</li>
            <li>Colaboradores sem visão.</li>
          </ul>
        </Modal.Body>
      </Modal>

      <Modal
        show={modalAberto}
        onHide={() => setModalAberto(false)}
        centered
        dialogClassName={ehHistoricoLogin ? 'relatorios-solar-dialog relatorios-solar-dialog-curto' : 'relatorios-solar-dialog'}
      >
        <Modal.Header closeButton>
          <Modal.Title>{relatorio?.nome ?? 'Relatório'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form id="relatorios-solar-filtros" onSubmit={aplicarFiltros} className="d-flex flex-column flex-grow-1 min-h-0">
            <div className={ehHistoricoLogin ? 'd-flex flex-column gap-3' : 'row g-3 mb-3 flex-shrink-0'}>
              <div className={ehHistoricoLogin ? undefined : 'col-md-6'}>
                <Form.Group className="mb-0" controlId="relatorios-solar-inicio">
                  <Form.Label>Data inicial</Form.Label>
                  <Form.Control
                    type="date"
                    min={DATA_INICIAL_MINIMA}
                    max={DATA_FINAL_MAXIMA}
                    value={rascunho.dataInicial}
                    onChange={(event) => atualizarData('dataInicial', event.target.value)}
                  />
                </Form.Group>
              </div>
              <div className={ehHistoricoLogin ? undefined : 'col-md-6'}>
                <Form.Group className="mb-0" controlId="relatorios-solar-fim">
                  <Form.Label>Data final</Form.Label>
                  <Form.Control
                    type="date"
                    min={DATA_INICIAL_MINIMA}
                    max={DATA_FINAL_MAXIMA}
                    value={rascunho.dataFinal}
                    onChange={(event) => atualizarData('dataFinal', event.target.value)}
                  />
                </Form.Group>
              </div>
              {ehHistoricoLogin && (
                <Form.Group className="mb-0">
                  <Form.Label htmlFor="relatorios-solar-usuario">Usuário</Form.Label>
                  <Select
                    inputId="relatorios-solar-usuario"
                    classNamePrefix="react-select"
                    options={opcoesUsuario}
                    value={usuarioSelecionado}
                    placeholder="Selecione um usuário..."
                    isClearable
                    isSearchable
                    noOptionsMessage={() => 'Nenhum usuário encontrado'}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    onChange={(opcao) => setRascunho((atual) => ({
                      ...atual,
                      usuarioId: opcao ? [opcao.value] : [],
                    }))}
                  />
                </Form.Group>
              )}
              {ehHistoricoLogin && (
                <Form.Check
                  id="relatorios-solar-calendario"
                  type="checkbox"
                  label="Exibir calendário"
                  checked={rascunho.exibirCalendario}
                  onChange={(event) => setRascunho((atual) => ({
                    ...atual,
                    exibirCalendario: event.target.checked,
                  }))}
                />
              )}
            </div>
            {!ehHistoricoLogin && (
            <div className="relatorios-solar-filtros-layout">
              <Nav
                variant="pills"
                activeKey={aba}
                onSelect={(chave) => setAba((chave as AbaFiltro) || 'defensor')}
                className="flex-column text-start"
              >
                {GRUPOS_FILTRO.map((grupo) => (
                  <Fragment key={grupo.id}>
                    <div className="relatorios-solar-filtros-grupo">---- {grupo.label}</div>
                    {grupo.itens.map((item) => (
                      <Nav.Item key={item.id}>
                        <Nav.Link eventKey={item.id} className="text-start">{item.label}</Nav.Link>
                      </Nav.Item>
                    ))}
                  </Fragment>
                ))}
              </Nav>

              <div className="relatorios-solar-filtros-painel">
                {aba === 'defensor' && (
                  <ListaFiltro
                    id="relatorios-solar-defensor"
                    label="Defensor"
                    values={rascunho.defensorId}
                    onChange={(valor) => alternarLista('defensorId', valor)}
                    opcoes={opcoesDefensor}
                  />
                )}
                {aba === 'servidor' && (
                  <ListaFiltro
                    id="relatorios-solar-servidor"
                    label="Servidor"
                    values={rascunho.servidorId}
                    onChange={(valor) => alternarLista('servidorId', valor)}
                    opcoes={opcoesServidor}
                  />
                )}
                {aba === 'tipo' && (
                  <ListaFiltro
                    id="relatorios-solar-tipo"
                    label="Tipo de Atendimento"
                    values={rascunho.tipoAtendimento}
                    onChange={(valor) => alternarLista('tipoAtendimento', valor)}
                    opcoes={opcoesTipo}
                  />
                )}
                {aba === 'area' && (
                  <ListaFiltro
                    id="relatorios-solar-area"
                    label="Área"
                    values={rascunho.areaId}
                    onChange={(valor) => alternarLista('areaId', valor)}
                    opcoes={opcoesArea}
                  />
                )}
                {aba === 'comarca' && (
                  <ListaFiltro
                    id="relatorios-solar-comarca"
                    label="Comarca"
                    values={rascunho.comarcaId}
                    onChange={(valor) => alternarLista('comarcaId', valor)}
                    opcoes={opcoesComarca}
                  />
                )}
                {aba === 'predio' && (
                  <ListaFiltro
                    id="relatorios-solar-predio"
                    label="Prédio"
                    values={rascunho.predioId}
                    onChange={(valor) => alternarLista('predioId', valor)}
                    opcoes={opcoesPredio}
                  />
                )}
                {aba === 'nucleo' && (
                  <ListaFiltro
                    id="relatorios-solar-nucleo"
                    label="Núcleo"
                    values={rascunho.nucleoId}
                    onChange={(valor) => alternarLista('nucleoId', valor)}
                    opcoes={opcoesNucleo}
                  />
                )}
                {aba === 'defensoria' && (
                  <ListaFiltro
                    id="relatorios-solar-defensoria"
                    label="Defensoria"
                    values={rascunho.defensoriaId}
                    onChange={(valor) => alternarLista('defensoriaId', valor)}
                    opcoes={opcoesDefensoria}
                  />
                )}
              </div>
            </div>
            )}
          </Form>
          {!ehHistoricoLogin && (
            <p className="small text-muted mb-0 mt-3 pt-2 border-top">{resumoFiltros}.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" type="button" onClick={limparRascunho}>
            Limpar
          </Button>
          <Button variant="primary" type="submit" form="relatorios-solar-filtros" disabled={periodoBloqueado}>
            Montar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

function ListaFiltro({
  id,
  label,
  values,
  onChange,
  opcoes,
}: {
  id: string;
  label: string;
  values: string[];
  onChange: (valor: string) => void;
  opcoes: OpcaoFiltro[];
}) {
  const ordenadas = useMemo(
    () => [...opcoes].sort((a, b) => a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' })),
    [opcoes],
  );
  const [busca, setBusca] = useState('');
  const selecionados = new Set(values);
  const termo = busca.trim().normalize('NFD').replace(/\p{Diacritic}/gu, '').toLocaleLowerCase('pt-BR');
  const visiveis = useMemo(() => {
    if (!termo) return ordenadas;
    return ordenadas.filter((opcao) =>
      opcao.label.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLocaleLowerCase('pt-BR').includes(termo),
    );
  }, [ordenadas, termo]);

  return (
    <div className="d-flex flex-column flex-grow-1 min-h-0">
      <Form.Label htmlFor={id} className="fw-semibold">{label}</Form.Label>
      <Form.Control
        id={id}
        type="search"
        placeholder="Pesquisar pelo nome"
        value={busca}
        onChange={(event) => setBusca(event.target.value)}
        className="mb-3"
        aria-label={`Pesquisar ${label}`}
      />
      {ordenadas.length === 0 ? (
        <span className="text-muted">Nenhuma opção</span>
      ) : visiveis.length === 0 ? (
        <span className="text-muted">Nenhum nome encontrado</span>
      ) : (
        <div className="d-flex flex-column gap-2 overflow-auto pe-1 flex-grow-1 min-h-0">
          {visiveis.map((opcao) => (
            <Form.Check
              key={opcao.value}
              id={`${id}-${opcao.value}`}
              type="checkbox"
              label={opcao.label}
              checked={selecionados.has(opcao.value)}
              onChange={() => onChange(opcao.value)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
